/**
 * cashFlowAggregate (#2611) — read the balance history from a SERVER-SIDE
 * daily aggregate instead of paging every ``cash_flow`` row into the browser.
 *
 * The feed used to fetch all cash_flow entities 1000 at a time behind a
 * 10-page runaway guard and silently truncate there. Once WF's twin passed
 * 19,344 flows that dropped 48.3% of them: every account's Ist series
 * flat-lined mid-April and the Hauptbank Bestand rendered wrong, while the
 * twin ledger itself was exact to the cent. Farmer-reported p1.
 *
 * The fix aggregates AT SOURCE on
 * ``account_id x value_date x status x planned_ref`` and rehydrates each
 * bucket into ONE synthetic cash_flow row. Nothing downstream changes:
 * ``cashFlowsToRecords`` / ``engineRecordsFrom`` / ``partitionEngineRecords``
 * / ``computeBalanceSeries`` all consume the synthetic rows exactly as they
 * consumed the raw ones.
 *
 * Why the curve stays byte-identical: the engine reads only ``value_date``
 * and the SIGNED amount, bucketed per day per account (``bucketByDay``). A
 * day's net sum is the same number whether it is added up in Postgres
 * ``numeric`` or in JS floats — and the DB path is the more exact of the two.
 *
 * Why each group key is load-bearing:
 *  - ``account_id``  — the #2005 per-account partition runs the engine once
 *    per Konto against its own anchor/KK-Limit.
 *  - ``value_date``  — the daily grain the curve is drawn on.
 *  - ``status``      — the aggregate must never mix roles in one bucket, so a
 *    row whose role is ambiguous and whose net silently blends booked money
 *    with committed money can never be built. Since #3107 ``contracted`` is
 *    read raw and no longer reaches the aggregate at all, so in practice this
 *    key now separates ``actual`` from any future non-excluded status — it is
 *    kept in the key regardless, because dropping it would re-introduce exactly
 *    that blend the moment another status stops being excluded.
 *  - ``planned_ref`` — a flow that settles a plan keeps its own bucket, so
 *    ``cashFlowToRecord`` rebuilds the SAME ``transaction_id`` a raw row
 *    would and the #1653 "payed supersedes its own plan" drop keeps working.
 *    Without it the curve would double-count every already-paid plan.
 *
 * Every status that needs PER-ROW IDENTITY is deliberately NOT aggregated —
 * see ``CASH_FLOW_AGGREGATE_EXCLUDED_STATUS`` below for the list, the two
 * families it covers (projection rows and, since #3107, ledger rows) and the
 * invariant it carries.
 *
 * Keep in lockstep with the Python twin,
 * ``backend/src/services/finance/liquiditaet_reads.aggregate_group_to_cash_flow``,
 * so the app, the MCP tools and the KI-Hofassistent read the same number.
 * NOTE this is a by-eye lockstep, not a by-path pin like the worker/UDB
 * mirrors: the two sides are asserted by SEPARATE unit tests restating the
 * same literals (``cashFlowAggregate.test.ts`` and
 * ``test_liquiditaet_feed_truncation.py``), so a change applied to only one
 * side stays green once its own literals move with it. Change both.
 */
import type { EntityAggregateItem } from '../../../services/twinService';

/** The aggregate group key. Order matters only for readability. */
export const CASH_FLOW_AGGREGATE_GROUP_BY = [
  'account_id',
  'value_date',
  'status',
  'planned_ref',
];

/**
 * Statuses read RAW rather than aggregated: every status that needs PER-ROW
 * IDENTITY in the browser. Two families:
 *
 * 1. **Every status that reaches the PROJECTION** — ``planned`` and
 *    ``scenario``. ``scenario`` is listed for a concrete reason: it maps to the
 *    ``planned`` role via ``STATUS_TO_ROLE``, so it DOES reach the projection,
 *    and aggregation collapses the per-row identity (``derived_fact_id``, label,
 *    category) that the ledger rows and the user-plan overlay below depend on.
 *
 * 2. **Every status the LEDGER renders as its own row** — ``contracted``
 *    (#3107). This is the family the pre-#3107 wording MISSED, and the miss is
 *    why #3065 shipped inert: contracted never reaches the projection, so
 *    "every status that reaches the PROJECTION" did not cover it and it was
 *    aggregated. ``aggregateGroupToCashFlow`` emits seven keys and NONE of
 *    ``settled_at`` / ``source_ref`` / ``ledger_category`` is among them, so an
 *    aggregated contracted row cannot carry #3065's settlement date, cannot be
 *    deduped on provenance (``contractedRecordsFrom``), and lands ``unsorted``
 *    in the Kategorien view — and N same-day invoices on one account collapse
 *    into ONE row of the wrong grain.
 *
 * NB (#2684): this is the BACKEND ``cash_flow.status`` enum value, which is
 * untouched — distinct from the retired FE weather/economy scenario feature. A
 * ``scenario``-status row is still a projection row and must still be read raw.
 * The pre-#2684 justification here ("aggregation drops the ``ledger_category``
 * the scenario spread is chosen by, so aggregating would move the optimistic /
 * pessimistic curves") died with that feature: the balance engine never read
 * ``ledger_category`` at all — only ``value_date`` / ``amount`` / ``status``,
 * all of which aggregation preserves.
 *
 * INVARIANT (load-bearing, see `engineRecordsFrom`): a USER-written plan
 * projects to a cash_flow with `status: 'planned'` — one of THESE — so an
 * aggregated row is never the projected copy of a user plan, and the user-plan
 * overlay drop (which keys on `derived_fact_id`, absent on aggregated rows)
 * stays correct. Remove a status from this list and that overlay silently stops
 * firing, double-counting every user-edited plan for an already-paid
 * transaction.
 *
 * ADDING a status (as #3107 did) is SAFE in that direction, and the asymmetry is
 * worth stating rather than leaving a future reader to re-derive it: adding only
 * moves rows OUT of the aggregate and into the raw read, which can never
 * manufacture a bucket the overlay drop would then miss. The cost of adding is
 * read VOLUME, not correctness — a raw status is page-walked against the
 * ``PAGE_LIMIT x MAX_PAGES`` = 10.000 ceiling in ``useLiquiditaetsplanung.ts``,
 * where ``dataQuality.truncated`` fails loud rather than silently short-reading.
 */
export const CASH_FLOW_AGGREGATE_EXCLUDED_STATUS = ['planned', 'scenario', 'contracted'];

/** Prefix of a bucket's synthetic ``cash_flow_id`` — distinct from any real
 *  id, so a bucket can never be mistaken for (or collide with) a written flow. */
export const CASH_FLOW_AGGREGATE_ID_PREFIX = 'cf-agg:';

/** One aggregate bucket → one synthetic ``cash_flow`` row. */
export function aggregateGroupToCashFlow(
  group: EntityAggregateItem,
): Record<string, unknown> {
  const keys = group.keys ?? {};
  const accountId = keys.account_id ?? null;
  const valueDate = keys.value_date ?? null;
  const status = keys.status ?? null;
  const plannedRef = keys.planned_ref ?? null;
  const row: Record<string, unknown> = {
    cash_flow_id:
      `${CASH_FLOW_AGGREGATE_ID_PREFIX}${accountId ?? ''}|${valueDate ?? ''}` +
      `|${status ?? ''}|${plannedRef ?? ''}`,
    account_id: accountId,
    value_date: valueDate,
    status,
    amount: group.sum == null ? 0 : group.sum,
    // Provenance so a reader can tell a bucket from a written flow.
    aggregated: true,
    source_count: group.count ?? 0,
  };
  // Omit the key entirely when absent — matches the raw-row shape.
  if (plannedRef) row.planned_ref = plannedRef;
  return row;
}

export function aggregateGroupsToCashFlows(
  groups: EntityAggregateItem[],
): Record<string, unknown>[] {
  return groups.map(aggregateGroupToCashFlow);
}
